/**
 * Simple QR code generator — no logo, clean and scannable.
 */
import QRCode from 'qrcode';

/**
 * Generate a plain QR code.
 * @param {string} data - The data to encode (URL, SKU, etc.)
 * @param {object} opts
 * @param {number} opts.size - Canvas size in pixels (default 472)
 * @returns {Promise<string>} PNG data URL
 */
export default async function generateBrandedQR(data, opts = {}) {
  const { size = 472 } = opts;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  await QRCode.toCanvas(canvas, data, {
    width: size,
    margin: 1,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#FFFFFF' },
  });

  return canvas.toDataURL('image/png');
}

/* ── Print sizes for Zebra ZD421 @ 300 DPI ── */
export const PRINT_SIZES = {
  small: { label: 'Small (25×25mm)', desc: 'Tight spaces, small parts', width: 295, height: 295, qrSize: 260 },
  standard: { label: 'Standard (40×40mm)', desc: 'Windshields, bumpers, general tagging', width: 472, height: 472, qrSize: 425 },
  large: { label: 'Large (50×50mm)', desc: 'Easy scan from distance, yard signs', width: 591, height: 591, qrSize: 531 },
  wide: { label: 'Wide (100×50mm)', desc: 'QR + unit info side by side', width: 1181, height: 591, qrSize: 472, hasInfo: true },
};

/**
 * Generate a wide label: QR on left, unit info on right.
 */
export async function generateWideLabel(unit, opts = {}) {
  const W = 1181, H = 591;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);

  // Left: QR
  const qrUrl = unit.sku ? `${window.location.origin}/?u=${unit.sku}` : (unit.sku || unit.id);
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, qrUrl, { width: 472, margin: 1, errorCorrectionLevel: 'M' });
  ctx.drawImage(qrCanvas, Math.round((591 - 472) / 2), Math.round((H - 472) / 2));

  // Divider
  ctx.strokeStyle = '#D1D5DB';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(591, 40);
  ctx.lineTo(591, H - 40);
  ctx.stroke();

  // Right: unit info
  const tx = 630;
  const name = `${unit.year || ''} ${unit.make || ''} ${unit.model || ''}`.trim() || unit.engineName || unit.partName || 'Unit';

  ctx.fillStyle = '#000000';
  ctx.font = 'bold 38px Arial, sans-serif';
  ctx.fillText(name, tx, 260);

  ctx.font = '28px "Courier New", monospace';
  ctx.fillStyle = '#374151';
  ctx.fillText(unit.sku || '', tx, 310);

  ctx.font = 'bold 22px Arial, sans-serif';
  ctx.fillStyle = '#9CA3AF';
  ctx.fillText('BIGSTAR · Sharjah', tx, 530);

  return canvas.toDataURL('image/png');
}

/**
 * Print a label image.
 */
export function printLabel(imageDataUrl, sizeKey = 'standard') {
  const size = PRINT_SIZES[sizeKey] || PRINT_SIZES.standard;
  const wMM = (size.width / 11.81).toFixed(1);
  const hMM = (size.height / 11.81).toFixed(1);

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <style>@page{size:${wMM}mm ${hMM}mm;margin:0}body{margin:0;padding:0}img{width:100%;height:100%;image-rendering:pixelated}</style>
    </head><body><img src="${imageDataUrl}"><script>window.onload=()=>{window.print();window.close()}<\/script></body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;width:0;height:0;border:none';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { try { iframe.contentWindow.print(); } catch {} setTimeout(() => iframe.remove(), 5000); };
}

/**
 * Download a label image.
 */
export function downloadLabel(imageDataUrl, sku, sizeKey) {
  const a = document.createElement('a');
  a.href = imageDataUrl;
  a.download = `QR_${sku}_${sizeKey}.png`;
  a.click();
}
